import { FC, useEffect } from 'react';
import { observer } from 'mobx-react';

import {
    Banner,
    BodyText,
    Button,
    Headline,
    Icon,
    Layout,
    Page,
    Spinner,
    Stack,
    TableColumn,
    TableNoRecords,
} from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { Table, TableCellProps } from '@servicetitan/table';

import { WebhookEventModelWithStatus } from '../../api/app.api';
import { WebhookManagementPageStore } from '../stores/webhook-mgmt-page.store';
import { AppPageHeader } from './app-page-header';

const EnableWebhookEventCell: FC<TableCellProps> = props => {
    const [store] = useDependencies(WebhookManagementPageStore);
    const webhookEvent = props.dataItem as WebhookEventModelWithStatus;
    return (
        <td>
            {webhookEvent.status === 'Disabled' && (
                <Button
                    xsmall
                    onClick={() => {
                        store.enableWebhook(webhookEvent);
                    }}
                >
                    Enable
                </Button>
            )}
        </td>
    );
};
EnableWebhookEventCell.displayName = 'EnableWebhookEventCell';

const WebhookEventsTable: FC = observer(() => {
    const [store] = useDependencies(WebhookManagementPageStore);
    return (
        <Table tableState={store.tableState} striped={false} sortable>
            <TableNoRecords>
                <div className="ta-center m-x-auto p-y-6" style={{ width: 420 }}>
                    <Icon name="perm_data_setting" size={32} className="m-b-2 c-neutral-80" />
                    <BodyText subdued size="small">
                        No version of this app uses webhooks.
                    </BodyText>
                </div>
            </TableNoRecords>
            <TableColumn field="displayName" title="Event Name" width={200} />
            <TableColumn field="url" title="Callback URL" />
            <TableColumn field="displayStatus" width={150} title="Status" />
            <TableColumn width={125} cell={EnableWebhookEventCell} />
        </Table>
    );
});
WebhookEventsTable.displayName = 'WebhookEventsTable';

export interface WebhookManagementPageProps {
    id: number;
}

export const WebhookManagementPage: FC<WebhookManagementPageProps> = provide({
    singletons: [WebhookManagementPageStore],
})(
    observer(({ id }) => {
        const [store] = useDependencies(WebhookManagementPageStore);

        useEffect(() => {
            store.load(id);
        }, [store, id]);

        const pageHeader = (
            <AppPageHeader
                id={id}
                publicId={store.publicId}
                appName={store.appName}
                deleted={false}
                errorBannerVisible={store.errorBannerVisible}
            />
        );

        if (store.errorBannerVisible) {
            return (
                <Page header={pageHeader}>
                    <Layout>
                        <Banner
                            status="critical"
                            icon
                            title="An error occurred while loading this page."
                        />
                    </Layout>
                </Page>
            );
        }

        return (
            <Page header={pageHeader}>
                <Layout>
                    <Headline>Webhook Events</Headline>
                    {store.loading ? (
                        <Stack direction="column" alignItems="center" className="m-t-5">
                            <Spinner size="large" />
                        </Stack>
                    ) : (
                        <WebhookEventsTable />
                    )}
                </Layout>
            </Page>
        );
    }),
);
WebhookManagementPage.displayName = 'WebhookManagementPage';
